<template>
  <div class="cc-dashboard">
    <div class="cc-topbar">
      <div class="cc-topbar-row">
        <div class="cc-topbar-left">
          <QuickLoanSearch v-if="azureId && token?.accessToken"
                           class="cc-quick-search"
                           :azureId="azureId"
                           :accessToken="token.accessToken"
                           @select="onQuickLoanSelected" />
        </div>

        <div class="cc-topbar-right">
          <div class="cc-topbar-pill" v-if="isBranchManager || isCorp">
            <i class="fa-duotone fa-sitemap"></i>
            <span class="cc-topbar-pill-text">Showing</span>

            <k-switch class="cc-scope-switch"
                      :onLabel="'All'"
                      :offLabel="'Mine'"
                      :checked="taskSwitchChecked"
                      @change="onTaskSwitchChange" />
          </div>
        </div>
      </div>
    </div>

    <div class="cc-layout-grid">
      <section class="cc-card cc-action-card">
        <div class="cc-card-header">
          <div class="cc-card-title">
            <i class="fa-duotone fa-triangle-exclamation"></i>
            Action Required
          </div>
        </div>

        <div class="cc-swap-area">
          <ActionRequiredCard v-if="azureId && token?.accessToken"
                              :azure-id="azureId"
                              :access-token="token.accessToken"
                              :task-switch-checked="taskSwitchChecked"
                              :new-apps-columns="newAppsColumns"
                              :expiring-locks-columns="expiringLocksColumns"
                              :open-task-columns="openTaskColumns"
                              :loan-launch-unread="loanLaunchUnread"
                              @open-grid="openGridDialog"
                              @open-disclosures="showDisclosures"
                              @open-loanlaunch="openLoanLaunchDrawer" />
        </div>
      </section>

      <section class="cc-card cc-pipeline-card" ref="pmCard">
        <div class="cc-card-header cc-smart-header">
          <div class="cc-smart-left">
            <div class="cc-card-title">
              <i class="fa-duotone fa-chart-simple"></i>
              Pipeline
            </div>
          </div>
        </div>

        <div class="cc-swap-area">
          <MilestoneOverviewCard v-if="azureId && token?.accessToken"
                                 :azure-id="azureId"
                                 :access-token="token.accessToken"
                                 :mine-only="!taskSwitchChecked"
                                 @loaded="onMilestonesLoaded"
                                 @open-milestone-loans="onMilestoneRowClicked" />
        </div>
      </section>

      <section class="cc-card cc-loan-tasks-card">
        <div class="cc-card-header">
          <div class="cc-card-title">
            <i class="fa-duotone fa-briefcase"></i>
            LoanFlow
          </div>
        </div>

        <div class="cc-task-grid cc-swap-area">
          <MyTasksCard v-if="azureId && token?.accessToken"
                       :azure-id="azureId"
                       :access-token="token.accessToken"
                       :is-grouped="isGrouped"
                       :open-task-columns="openTaskColumns"
                       :internal-task-columns="internalTaskColumns"
                       @open-grid="openGridDialog"
                       section="loan"
                       :taskSwitchChecked="taskSwitchChecked" />
        </div>
      </section>

      <section class="cc-card cc-closing-card">
        <ClosingNext7DaysCard v-if="azureId && token?.accessToken"
                              :azure-id="azureId"
                              :access-token="token.accessToken"
                              :mine-only="!taskSwitchChecked"
                              @loaded="onClosingLoaded"
                              @open-loan="openLoanTasks" />
      </section>

      <section class="cc-card cc-my-open-loanblock">
        <LoansRecentlyOpened v-if="azureId && token?.accessToken"
                             :azureId="azureId"
                             :authToken="token.accessToken"
                             :lastUpdatedAt="lastUpdated.recentLoans"
                             @refreshed="markUpdated('recentLoans')"
                             @openLoan="openLoanTasks" />
      </section>
    </div>

    <GridDialog :open="gridDialog"
                :title="pipelineTitle"
                :dialogType="gridDialogType"
                :columns="gridDialogColumns"
                :data-items="gridDialogData"
                :selected-field="selectedField"
                :send-noti-button="sendNotiButton"
                :incomplete-app-switch="incompleteAppSwitch"
                :incomplete-app-switch-checked="incompleteAppSwitchChecked"
                @close="onGridDialogClose"
                @row-click="navigateToLoan"
                @selection-change="onSelectionChange"
                @header-selection-change="onHeaderSelectionChange"
                @export-excel="exportExcel"
                @complete-task="completeInternalTask" />

    <LoanListModal :open="milestoneModalOpen"
                   :title="milestoneModalTitle"
                   :subtitle="milestoneModalSubtitle"
                   :loans="milestoneModalLoans"
                   @close="closeMilestoneModal"
                   @open-loan="openLoanTasks" />

    <ConfirmDialog v-if="showConfirmDialog"
                   :title="confirmTitle"
                   :message="confirmMsg"
                   :note="confirmNote"
                   @confirm="onActionConfirmed"
                   @cancel="toggleConfirmDialog" />

    <ErrorDialog v-if="showErrorDialog"
                 :title="errorTitle"
                 :message="errorMsg"
                 @close="toggleErrorDialog" />

    <!-- Toast notification -->
    <transition name="cc-toast">
      <div v-if="toast.show" class="cc-toast" :class="`cc-toast--${toast.type}`">
        <i class="fa-solid" :class="toast.type === 'success' ? 'fa-circle-check' : 'fa-circle-xmark'"></i>
        {{ toast.message }}
      </div>
    </transition>

    <div
      v-if="!isAIDrawerOpen"
      class="cc-swiftai-fab"
      :class="{ dragging: _isDraggingFab }"
      :style="fabStyle"
      @pointerdown="startFabDrag"
      @click="onFabClick"
      role="button"
      tabindex="0"
    >
      <i class="fa-duotone fa-wand-magic-sparkles"></i>
      swiftChat
    </div>

    <div v-if="(isAIDrawerOpen || isLoanLaunchDrawerOpen) && viewportWidth > 925"
         class="cc-dock-overlay"
         @click="closeAllDocks" />

    <div v-if="isAIDrawerOpen && viewportWidth > 925"
         class="cc-ai-dock open"
         :class="{ 'is-interacting': isDockInteracting }"
         :style="{
        width: aiDockWidth + 'px',
        right: isLoanLaunchDrawerOpen ? loanLaunchDockWidth + 12 + 'px' : '0px'
      }"
         @click.stop>
      <div class="cc-ai-resize-handle" @pointerdown="startResize"></div>
      <div v-if="isDockInteracting" class="cc-drag-shield" @click.stop></div>

      <AIFlyoutDrawer :open="true"
                      :docked="true"
                      :width="aiDockWidth"
                      :tabs="AITabs"
                      :selected-tab="selectedTab"
                      :uw-guides-url="uwGuidesUrl"
                      :doc-analysis-url="docAnalysisUrl"
                      :pipeline-url="pipelineUrl"
                      :productFinder-url="productFinderUrl"
                      :azure-id="azureId"
                      :user-name="userName"
                      @close="isAIDrawerOpen = false"
                      @tab-selected="onTabSelected" />
    </div>

    <AIFlyoutDrawer v-if="isAIDrawerOpen && viewportWidth <= 925"
                    :open="true"
                    :width="aiDockWidth"
                    :tabs="AITabs"
                    :selected-tab="selectedTab"
                    :uw-guides-url="uwGuidesUrl"
                    :doc-analysis-url="docAnalysisUrl"
                    :pipeline-url="pipelineUrl"
                    :productFinder-url="productFinderUrl"
                    :azure-id="azureId"
                    :user-name="userName"
                    @close="isAIDrawerOpen = false"
                    @tab-selected="onTabSelected" />

    <div v-if="isLoanLaunchDrawerOpen && viewportWidth > 925"
         class="cc-ai-dock open"
         :class="{ 'is-interacting': isDockInteracting }"
         :style="{ width: loanLaunchDockWidth + 'px', right: '0px' }"
         @click.stop>
      <div class="cc-ai-resize-handle" @pointerdown="startLoanLaunchResize"></div>
      <div v-if="isDockInteracting" class="cc-drag-shield" @click.stop></div>

      <LoanLaunchChatDrawer :open="true"
                            :width="loanLaunchDockWidth"
                            :messages="loanLaunchMessages"
                            :unread-total="loanLaunchUnread"
                            :is-fetching="isLoanLaunchFetching"
                            @close="isLoanLaunchDrawerOpen = false"
                            @fetch-more="fetchLoanLaunchMessages(false)"
                            @message-click="onLoanLaunchMessageClicked" />
    </div>

    <LoanLaunchChatDrawer v-if="isLoanLaunchDrawerOpen && viewportWidth <= 925"
                          :open="true"
                          :width="loanLaunchDockWidth"
                          :messages="loanLaunchMessages"
                          :unread-total="loanLaunchUnread"
                          :is-fetching="isLoanLaunchFetching"
                          @close="isLoanLaunchDrawerOpen = false"
                          @fetch-more="fetchLoanLaunchMessages(false)"
                          @message-click="onLoanLaunchMessageClicked" />
  </div>
</template>

<script>
  import auth from "@/authConfig.js"
import sidebarChatBridge from "@/helpers/sidebarChatBridge.js"
  import apiService from "@/api/api.service.js"

  import { Checkbox } from "@progress/kendo-vue-inputs"
  import { Button } from "@progress/kendo-vue-buttons"
  import { Switch } from "@progress/kendo-vue-inputs"

  import GridDialog from "@/components/commandCenter/GridDialog.vue"
  import ConfirmDialog from "@/components/commandCenter/ConfirmDialog.vue"
  import ErrorDialog from "@/components/commandCenter/ErrorDialog.vue"
  import AIFlyoutDrawer from "@/components/commandCenter/AIFlyoutDrawer.vue"
  import MyTasksCard from "@/components/commandCenter/MyTasksCard.vue"
  import ActionRequiredCard from "@/components/commandCenter/ActionRequiredCard.vue"

  import LoanLaunchChatDrawer from "@/components/commandCenter/LoanLaunchChatDrawer.vue"
  import LoansRecentlyOpened from "@/components/conditionCommand/InitialUwRecentLoansCard.vue"
  import QuickLoanSearch from "@/components/commandCenter/QuickLoanSearch.vue"

  import MilestoneOverviewCard from "@/components/commandCenter/MilestoneOverviewCard.vue"
  import ClosingNext7DaysCard from "@/components/commandCenter/ClosingNext7DaysCard.vue"
  import LoanListModal from "@/components/commandCenter/LoanListModal.vue"

  import { getNestedValue, formatDate } from "@/helpers/formatters.js"

  const TITLE_TYPE_MAP = {
    "Outstanding Tasks": "borrowerTask",
    "Pending Tasks": "borrowerTask",
    "Pending Borrower Signature": "borrowerTask",
    "Borrower Outstanding Tasks": "borrowerTask",

    "Due Today": "internalTask",
    "Past Due": "internalTask",
    "Open Loan Tasks": "internalTask",
    "Outstanding Loan Tasks": "internalTask",
    "Tasks I Assigned": "internalTask",
    "Team Tasks": "internalTask",
    "Task Priority": "internalTask",

    "New Applications": "application",
    "Incomplete Applications": "application",

    "Expiring Locks": "loan",
    "Unlocked Loans": "loan"
  }

  export default {
  mixins: [sidebarChatBridge],
    name: "LoanOfficerAssistantDashboard",

    components: {
      MyTasksCard,
      GridDialog,
      ConfirmDialog,
      ErrorDialog,
      AIFlyoutDrawer,
      checkbox: Checkbox,
      kbutton: Button,
      "k-switch": Switch,
      ActionRequiredCard,
      LoanLaunchChatDrawer,
      LoansRecentlyOpened,
      QuickLoanSearch,
      MilestoneOverviewCard,
      ClosingNext7DaysCard,
      LoanListModal
    },

    data() {
      const w = typeof window !== "undefined" ? window.innerWidth : 0
      const h = typeof window !== "undefined" ? window.innerHeight : 0

      return {
        viewportWidth: w,
        viewportHeight: h,

        token: null,
        azureId: "",
        userName: "",

        isBranchManager: false,
        isCorp: false,
        isGrouped: false,

        taskSwitchChecked: false,

        milestoneModalOpen: false,
        milestoneModalTitle: "",
        milestoneModalSubtitle: "",
        milestoneModalLoans: [],

        aiDockWidth: 420,
        aiDockMinWidth: 740,
        aiDockMaxWidth: 9999,
        _isResizingDock: false,
        _dockPointerId: null,

        isAIDrawerOpen: false,
        AITabs: [
          { title: "Search UW Guidelines", content: "UWGuides" },
          { title: "Document Analyzer", content: "docsum" },
          { title: "Pipeline Insights", content: "pipeline" }
        ],
        selectedTab: 0,

        gridDialog: false,
        pipelineTitle: "",
        gridDialogType: "",
        gridDialogColumns: [],
        gridDialogData: [],
        sendNotiButton: false,
        incompleteAppSwitch: false,
        incompleteAppSwitchChecked: false,

        selectedField: "selected",
        tasksToSend: [],

        showConfirmDialog: false,
        confirmTitle: "Please Confirm",
        confirmMsg: "",
        confirmNote: "",
        confirmFunction: null,

        showErrorDialog: false,
        errorTitle: "Error",
        errorMsg: "",

        isLoanLaunchDrawerOpen: false,
        loanLaunchDockWidth: 740,
        loanLaunchDockMinWidth: 740,
        loanLaunchDockMaxWidth: 9999,
        _isResizingLoanLaunchDock: false,
        _loanLaunchPointerId: null,

        loanLaunchMessages: [],
        loanLaunchUnread: 0,
        loanLaunchCachedQuery: null,
        isLoanLaunchFetching: false,
        loanLaunchIntervalId: null,

        localLogo: require("@/assets/LoanLaunchLogo.png"),
        lastUpdated: {
          recentLoans: null
        },

        cardWidth: 0,

      fabPos: { x: null, y: null },
      _isDraggingFab: false,
      _fabPointerId: null,
      _fabStart: { x: 0, y: 0 },
      _fabStartPos: { x: 0, y: 0 },
      _fabMoved: false,

      // Toast
      toast: { show: false, message: "", type: "success" },
      _toastTimeout: null,
    }
  },

    computed: {
      isDockInteracting() {
        return this._isResizingDock || this._isResizingLoanLaunchDock
      },

      fabStyle() {
        const x = this.fabPos?.x
        const y = this.fabPos?.y
        if (x == null || y == null) return {}
        return {
          left: `${x}px`,
          top: `${y}px`,
          right: "auto",
          bottom: "auto"
        }
      },

      openTaskColumns() {
        return [
          { field: "f364", title: "Loan Number", sortable: true, filterable: true },
          { field: "assignedto", title: "Assigned To", sortable: true, filterable: true, cell: "assignedToCell" },
          { field: "borrower_name", title: "Borrower Name", sortable: true, filterable: true },
          { field: "title", title: "Title", sortable: true, filterable: true },
          { field: "created", title: "Date Created", sortable: true, filterable: true },
          { field: "age", title: "Task Age", sortable: true, filterable: true },
          { field: "cust17fv", title: "Est. Closing Date", sortable: true, filterable: true }
        ]
      },

      newAppsColumns() {
        return [
          { field: "f364", title: "Loan Number", sortable: true, filterable: true },
          { field: "borrower_name", title: "Borrower Name", sortable: true, filterable: true },
          { field: "f11", title: "Address", sortable: true, filterable: true },
          { field: "logmsdatestarted", title: "Date", sortable: true, filterable: true }
        ]
      },

      expiringLocksColumns() {
        return [
          { field: "f364", title: "Loan Number", sortable: true, filterable: true },
          { field: "name", title: "Name", sortable: true, filterable: true },
          { field: "f2149", title: "Lock Date", sortable: true, filterable: true },
          { field: "f2151", title: "Lock Expiration Date", sortable: true, filterable: true }
        ]
      },

      internalTaskColumns() {
        return [
          { field: "loannumber", title: "Loan Number", sortable: true, filterable: true },
          { field: "borrowername", title: "Borrower Name", sortable: true, filterable: true },
          { field: "title", title: "Title", sortable: true, filterable: true },
          { field: "assignedto", title: "Assigned To", sortable: true, filterable: true },
          { field: "created", title: "Date Created", sortable: true, filterable: true },
          { field: "followupdate", title: "Follow Up Date", sortable: true, filterable: true },
          { field: "status", title: "Status", sortable: true, filterable: true }
        ]
      },

      uwGuidesUrl() {
        const hostname = typeof window !== "undefined" ? window.location.hostname : ""
        const base =
          hostname.startsWith("dev.") || hostname === "localhost"
            ? 'https://devai.goldstarfinancial.com'
            : "https://ai.swiftapp.ai"
        return `${base}/chat/gpt/iframe/`
      },

      docAnalysisUrl() {
        const hostname = typeof window !== "undefined" ? window.location.hostname : ""
        const base =
          hostname.startsWith("dev.") || hostname === "localhost"
            ? 'https://devai.goldstarfinancial.com'
            : "https://ai.swiftapp.ai"
        return `${base}/chat/summary`
      },

      pipelineUrl() {
        const hostname = typeof window !== "undefined" ? window.location.hostname : ""
        const base =
          hostname.startsWith("dev.") || hostname === "localhost"
            ? 'https://devai.goldstarfinancial.com'
            : "https://ai.swiftapp.ai"
        return `${base}/chat/gpt/iframe/pipeline`
      },
      productFinderUrl() {
        const hostname = typeof window !== "undefined" ? window.location.hostname : ""
        const base =
          hostname.startsWith("dev.") || hostname === "localhost"
            ? 'https://devai.goldstarfinancial.com'
            : "https://ai.swiftapp.ai"
        return `${base}/iframe/productFinder`
      }
    },

  methods: {
    // ── Toast ────────────────────────────────────────────────────────
    showToast(message, type = "success") {
      if (this._toastTimeout) clearTimeout(this._toastTimeout)
      this.toast = { show: true, message, type }
      this._toastTimeout = setTimeout(() => { this.toast.show = false }, 3000)
    },

    closeMilestoneModal() {
      this.milestoneModalOpen = false
      this.milestoneModalTitle = ""
      this.milestoneModalSubtitle = ""
      this.milestoneModalLoans = []
    },

      onMilestoneRowClicked(payload) {
        const m = payload?.milestone
        const loans = payload?.loans || []
        const mineOnly = !!payload?.mineOnly

        this.milestoneModalLoans = loans
        this.milestoneModalTitle = m?.msstatus || "Milestone Loans"
        this.milestoneModalSubtitle = mineOnly ? "Mine only" : "All loans"
        this.milestoneModalOpen = true
      },

      getNestedValue,
      formatDate,

      closeAllDocks() {
        this.isAIDrawerOpen = false
        this.isLoanLaunchDrawerOpen = false
      },

      onClosingLoaded(payload) {
        this.$emit("closing-loaded", payload)
      },

      onMilestonesLoaded(payload) {
        this.$emit("milestones-loaded", payload)
      },

      onFabClick() {
        if (this._fabMoved) return
        this.isAIDrawerOpen = true
      },

      _fabClamp(pos) {
        const btnW = 168
        const btnH = 54
        const pad = 10

        const maxX = Math.max(pad, (this.viewportWidth || window.innerWidth) - btnW - pad)
        const maxY = Math.max(pad, (this.viewportHeight || window.innerHeight) - btnH - pad)

        return {
          x: Math.min(maxX, Math.max(pad, pos.x)),
          y: Math.min(maxY, Math.max(pad, pos.y))
        }
      },

      _saveFabPos() {
        try {
          if (this.fabPos?.x == null || this.fabPos?.y == null) return
          localStorage.setItem("cc_swiftchat_fab_pos", JSON.stringify(this.fabPos))
        } catch (e) { }
      },

      _loadFabPos() {
        try {
          const raw = localStorage.getItem("cc_swiftchat_fab_pos")
          if (!raw) return
          const parsed = JSON.parse(raw)
          if (parsed && typeof parsed.x === "number" && typeof parsed.y === "number") {
            this.fabPos = this._fabClamp(parsed)
          }
        } catch (e) { }
      },

      startFabDrag(e) {
        if (!e) return
        if (this.isAIDrawerOpen) return

        this._isDraggingFab = true
        this._fabMoved = false
        this._fabPointerId = e.pointerId
        this._fabStart = { x: e.clientX, y: e.clientY }

        const defaultX = (this.viewportWidth || window.innerWidth) - 18 - 168
        const defaultY = (this.viewportHeight || window.innerHeight) - 18 - 54
        const current = this.fabPos?.x != null && this.fabPos?.y != null ? this.fabPos : { x: defaultX, y: defaultY }
        this._fabStartPos = { x: current.x, y: current.y }

        if (e.target && e.pointerId != null && e.target.setPointerCapture) {
          try {
            e.target.setPointerCapture(e.pointerId)
          } catch (err) { }
        }

        window.addEventListener("pointermove", this.onFabDragMove, { passive: false })
        window.addEventListener("pointerup", this.stopFabDrag, { passive: true })
        window.addEventListener("pointercancel", this.stopFabDrag, { passive: true })
      },

      onFabDragMove(e) {
        if (!this._isDraggingFab) return
        if (!e) return
        e.preventDefault()

        const dx = e.clientX - this._fabStart.x
        const dy = e.clientY - this._fabStart.y
        if (Math.abs(dx) + Math.abs(dy) > 6) this._fabMoved = true

        const next = this._fabClamp({
          x: this._fabStartPos.x + dx,
          y: this._fabStartPos.y + dy
        })

        this.fabPos = next
      },

      stopFabDrag() {
        if (!this._isDraggingFab) return

        this._isDraggingFab = false
        this._fabPointerId = null

        window.removeEventListener("pointermove", this.onFabDragMove)
        window.removeEventListener("pointerup", this.stopFabDrag)
        window.removeEventListener("pointercancel", this.stopFabDrag)

        this._saveFabPos()

        setTimeout(() => {
          this._fabMoved = false
        }, 0)
      },

      onQuickLoanSelected(loan) {
        const isDev = window.location.hostname === "localhost" || window.location.hostname === "dev.swiftapp.ai"
        const baseUrl = isDev ? "https://dev-swift.goldstarfinancial.com" : "https://swift.goldstarfinancial.com"
        window.open(`${baseUrl}/LoanTasks/?id=${loan.id}`, "_blank")
      },

      markUpdated(key) {
        this.lastUpdated = { ...this.lastUpdated, [key]: new Date().toISOString() }
      },

      openLoanTasks(loanIdOrLoan, condId = null) {
        const loanId = typeof loanIdOrLoan === "string" ? loanIdOrLoan : loanIdOrLoan?.id
        if (!loanId) return

        const isDev = window.location.hostname === "localhost" || window.location.hostname === "dev.swiftapp.ai"
        const baseUrl = isDev ? "https://dev-swift.goldstarfinancial.com" : "https://swift.goldstarfinancial.com"

        window.open(`${baseUrl}/LoanTasks/?id=${loanId}${condId ? `&condid=${condId}` : ""}`, "_blank")
      },

      openLoanLaunchDrawer() {
        this.isLoanLaunchDrawerOpen = true
      },

      async fetchLoanLaunchUnread() {
        if (!this.azureId || !this.token?.accessToken) return

        const req = {
          query: this.loanLaunchCachedQuery,
          AzureId: this.azureId,
          LastMessageId: null,
          LastSentDate: null,
          TopCount: 0
        }

        const res = await apiService.GetUnreadLoanLaunchMessages(req, this.token.accessToken)
        this.loanLaunchUnread = res?.data?.data?.totalUnread ?? 0
      },

      async fetchLoanLaunchMessages(fetchNewer = false) {
        if (!this.azureId || !this.token?.accessToken) return
        if (this.isLoanLaunchFetching) return

        this.isLoanLaunchFetching = true

        let referenceMessage = null
        if (this.loanLaunchMessages.length > 0) {
          referenceMessage = fetchNewer ? this.loanLaunchMessages[0] : this.loanLaunchMessages[this.loanLaunchMessages.length - 1]
        }

        const req = {
          query: this.loanLaunchCachedQuery,
          AzureId: this.azureId,
          LastMessageId: referenceMessage?.id,
          LastSentDate: referenceMessage?.sent,
          TopCount: 35,
          IsForward: fetchNewer
        }

        const res = await apiService.GetLoanLaunchMessagesWithReadStatus(req, this.token.accessToken)

        if (res?.data?.data?.query) this.loanLaunchCachedQuery = res.data.data.query

        const newMessages = res?.data?.data?.messages ?? []

        if (newMessages.length > 0) {
          if (fetchNewer) this.loanLaunchMessages.unshift(...newMessages)
          else this.loanLaunchMessages.push(...newMessages)
        }

        this.isLoanLaunchFetching = false
      },

      onLoanLaunchMessageClicked(message) {
        const baseUrl = "https://swift.goldstarfinancial.com"
        window.open(`${baseUrl}/ConsumerPortal/?id=${message.loanid}`, "_blank")

        setTimeout(() => {
          const idx = this.loanLaunchMessages.findIndex((m) => m.id === message.id)
          if (idx !== -1) this.loanLaunchMessages[idx].isread = true
        }, 250)
      },

      onTaskSwitchChange() {
        this.taskSwitchChecked = !this.taskSwitchChecked
      },

      startResize(e) {
        if (!this.isAIDrawerOpen) return
        if (!e) return
        this._isResizingDock = true
        this._dockPointerId = e.pointerId

        if (e.target && e.pointerId != null && e.target.setPointerCapture) {
          try {
            e.target.setPointerCapture(e.pointerId)
          } catch (err) { }
        }

        document.body.classList.add("cc-resize-grabbing")
        window.addEventListener("pointermove", this.onResizeMove, { passive: false })
        window.addEventListener("pointerup", this.stopResize, { passive: true })
        window.addEventListener("pointercancel", this.stopResize, { passive: true })
      },

      onResizeMove(e) {
        if (!this._isResizingDock) return
        if (!e) return
        e.preventDefault()

        const rightOffset = this.isLoanLaunchDrawerOpen ? this.loanLaunchDockWidth + 12 : 0
        const dockRightEdge = this.viewportWidth - rightOffset
        const proposed = dockRightEdge - e.clientX

        const maxAllowed = Math.min(this.aiDockMaxWidth, this.viewportWidth - rightOffset - 24)
        this.aiDockWidth = Math.max(this.aiDockMinWidth, Math.min(maxAllowed, proposed))
      },

      stopResize() {
        if (!this._isResizingDock) return

        this._isResizingDock = false
        this._dockPointerId = null
        document.body.classList.remove("cc-resize-grabbing")

        window.removeEventListener("pointermove", this.onResizeMove)
        window.removeEventListener("pointerup", this.stopResize)
        window.removeEventListener("pointercancel", this.stopResize)

        localStorage.setItem("cc_ai_dock_width", String(this.aiDockWidth))
      },

      startLoanLaunchResize(e) {
        if (!this.isLoanLaunchDrawerOpen) return
        if (!e) return
        this._isResizingLoanLaunchDock = true
        this._loanLaunchPointerId = e.pointerId

        if (e.target && e.pointerId != null && e.target.setPointerCapture) {
          try {
            e.target.setPointerCapture(e.pointerId)
          } catch (err) { }
        }

        document.body.classList.add("cc-resize-grabbing")
        window.addEventListener("pointermove", this.onLoanLaunchResizeMove, { passive: false })
        window.addEventListener("pointerup", this.stopLoanLaunchResize, { passive: true })
        window.addEventListener("pointercancel", this.stopLoanLaunchResize, { passive: true })
      },

      onLoanLaunchResizeMove(e) {
        if (!this._isResizingLoanLaunchDock) return
        if (!e) return
        e.preventDefault()

        const maxAllowed = Math.min(this.loanLaunchDockMaxWidth, this.viewportWidth - 24)
        const proposed = this.viewportWidth - e.clientX

        this.loanLaunchDockWidth = Math.max(this.loanLaunchDockMinWidth, Math.min(maxAllowed, proposed))
      },

      stopLoanLaunchResize() {
        if (!this._isResizingLoanLaunchDock) return

        this._isResizingLoanLaunchDock = false
        this._loanLaunchPointerId = null
        document.body.classList.remove("cc-resize-grabbing")

        window.removeEventListener("pointermove", this.onLoanLaunchResizeMove)
        window.removeEventListener("pointerup", this.stopLoanLaunchResize)
        window.removeEventListener("pointercancel", this.stopLoanLaunchResize)

        localStorage.setItem("cc_loanlaunch_dock_width", String(this.loanLaunchDockWidth))
      },

      onTabSelected(selected) {
        this.selectedTab = selected
      },

      _typeFromTitle(title) {
        const t = (title || "").trim()
        if (TITLE_TYPE_MAP[t]) return TITLE_TYPE_MAP[t]
        if (t.startsWith("Tasks Created on ")) return "internalTask"
        if (t.startsWith("Bottleneck:")) return "internalTask"
        return "unknown"
      },

      openGridDialog(payload) {
        const title = payload?.title || ""

        this.gridDialog = true
        this.pipelineTitle = title

        this.gridDialogType = payload?.type || payload?.dialogType || this._typeFromTitle(title)

        const raw = Array.isArray(payload?.data) ? payload.data : []
        this.gridDialogData = raw.map((x) => ({ ...x, [this.selectedField]: false }))

        this.gridDialogColumns = Array.isArray(payload?.columns) ? payload.columns : []

        this.sendNotiButton = !!payload?.sendNotiButton
        this.incompleteAppSwitch = !!payload?.incompleteAppSwitch
        this.incompleteAppSwitchChecked = !!payload?.incompleteAppSwitchChecked

        this.tasksToSend = []
      },

    navigateToLoan(e) {
      console.log(e)
      const dataItem = e?.dataItem ?? e?.dataItem?.dataItem ?? e?.dataItem
      if (!dataItem) return

        const type = this.gridDialogType

      if (type === "borrowerTask" || type === "internalTask") {
        if (dataItem.tasklink)
        { window.open(String(dataItem.tasklink), "_blank")
        return
      }
      }

        if (type === "application") {
          const id = dataItem.appid || dataItem.id || dataItem.Id
          if (!id) return
          window.open(`https://swift.goldstarfinancial.com/ConsumerPortal?id=${id}&swiftprocessing=true`, "_blank")
          return
        }

        if (type === "loan") {
          const id = dataItem.id || dataItem.loanid || dataItem.f364
          if (!id) return
          window.open(`https://swift.goldstarfinancial.com/ConsumerPortal/?id=${id}`, "_blank")
        }
      },

      onGridDialogClose() {
        this.gridDialog = false
        this.pipelineTitle = ""
        this.gridDialogType = ""
        this.gridDialogColumns = []
        this.gridDialogData = []
        this.tasksToSend = []
        this.sendNotiButton = false
        this.incompleteAppSwitch = false
      },

      onSelectionChange(dataItem) {
        if (!dataItem) return

        dataItem[this.selectedField] = !dataItem[this.selectedField]

        const id = dataItem?.id || dataItem?.task_id
        if (!id) return

        if (dataItem[this.selectedField]) {
          const exists = this.tasksToSend.some((t) => (t?.id || t?.task_id) === id)
          if (!exists) this.tasksToSend.push(dataItem)
        } else {
          this.tasksToSend = this.tasksToSend.filter((t) => (t?.id || t?.task_id) !== id)
        }
      },

      onHeaderSelectionChange(e) {
        const checked = e?.event?.target?.checked
        if (checked == null) return

        this.gridDialogData = this.gridDialogData.map((item) => ({ ...item, [this.selectedField]: checked }))

        if (checked) {
          const map = new Map()
          this.gridDialogData.forEach((item) => {
            const id = item?.id || item?.task_id
            if (id) map.set(id, item)
          })
          this.tasksToSend = Array.from(map.values())
        } else {
          this.tasksToSend = []
        }
      },

      exportExcel(localData) {
        this.$emit("export-excel", localData)
      },

      async completeInternalTask(task) {
        if (!task || task.completed) return

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          if (!tokenResponse || !tokenResponse.accessToken) throw new Error("Failed to acquire token")

          const options = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
          }

          const currentDateTime = new Intl.DateTimeFormat("en-US", options).format(new Date())
          const currentUser = tokenResponse.account.name

          const updatedParams = {
            ...task,
            completed: true,
            status: "Completed",
            lastUpdated: `${currentDateTime} by ${currentUser}`
          }

        await apiService.UpsertInternalAssignedTask(updatedParams, tokenResponse.accessToken)

        // Remove from grid and show toast
        const id = task?.id || task?.task_id
        this.gridDialogData = this.gridDialogData.filter(t => (t?.id || t?.task_id) !== id)
        this.showToast(`"${task.title || "Task"}" marked complete`)
      } catch (error) {
        this.errorTitle = "Error Completing Task"
        this.errorMsg = error.response?.data?.data || error.message || "Failed to complete task"
        this.toggleErrorDialog()
      }
    },

      toggleConfirmDialog() {
        this.showConfirmDialog = !this.showConfirmDialog
        if (!this.showConfirmDialog) {
          this.confirmMsg = ""
          this.confirmNote = ""
          this.confirmFunction = null
        }
      },

      toggleErrorDialog() {
        this.showErrorDialog = !this.showErrorDialog
        if (!this.showErrorDialog) {
          this.errorMsg = ""
          this.errorTitle = ""
        }
      },

      onActionConfirmed() {
        this[this.confirmFunction]?.()
        this.toggleConfirmDialog()
      },

      showDisclosures() {
        window.open("https://www.encompassloconnect.com/package-pipeline", "_blank")
      },

      async GetRoles() {
        try {
          const raw = localStorage.getItem('active_user_roles')
          let roles = []
          if (raw) {
            try { roles = JSON.parse(raw) } catch { roles = String(raw).split(',') }
          }
          this.isBranchManager = roles.includes("Branch Manager")
          this.isCorp = roles.includes("Corp")
        } catch (e) { }
      },

      async checkGrouping() {
        try {
          const res = await apiService.GetGrouping(this.azureId, this.token.accessToken)
          this.isGrouped = (res?.data?.data?.length || 0) > 0
        } catch (e) { }
      },

      _updateViewport() {
        if (typeof window === "undefined") return
        this.viewportWidth = window.innerWidth
        this.viewportHeight = window.innerHeight

        if (!this.isAIDrawerOpen) {
          if (this.fabPos?.x != null && this.fabPos?.y != null) {
            this.fabPos = this._fabClamp(this.fabPos)
            this._saveFabPos()
          }
        }
      },

      _initDockWidths() {
        const vw = this.viewportWidth || window.innerWidth
        const target = Math.round(vw * 0.63)

        const ai = Math.max(this.aiDockMinWidth, Math.min(this.aiDockMaxWidth, target))
        const ll = Math.max(this.loanLaunchDockMinWidth, Math.min(this.loanLaunchDockMaxWidth, target))

        const savedAi = parseInt(localStorage.getItem("cc_ai_dock_width"), 10)
        const savedLl = parseInt(localStorage.getItem("cc_loanlaunch_dock_width"), 10)

        if (!isNaN(savedAi)) this.aiDockWidth = savedAi
        else this.aiDockWidth = ai

        if (!isNaN(savedLl)) this.loanLaunchDockWidth = savedLl
        else this.loanLaunchDockWidth = ll
      },

      _updateCardWidth() {
        this.$nextTick(() => {
          const el = this.$refs.pmCard
          if (!el || !el.getBoundingClientRect) return

          const cs = getComputedStyle(el)
          const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight)
          const w = Math.max(0, el.clientWidth - padX)

          if (Math.abs(w - this.cardWidth) >= 1) this.cardWidth = w
        })
      }
    },

    async mounted() {
      this.token = await auth.acquireTokenResponse()
      this.azureId = localStorage.getItem("azure_id")
      this.userName = this.token?.account?.name || localStorage.getItem("user_name") || ""

      this._updateViewport()
      this._initDockWidths()
      this._loadFabPos()

      await this.GetRoles()
      await this.checkGrouping()

      await this.fetchLoanLaunchUnread()
      await this.fetchLoanLaunchMessages(true)



      window.addEventListener("resize", this._updateViewport, { passive: true })

      this._updateCardWidth()
      window.addEventListener("resize", this._updateCardWidth, { passive: true })
      setTimeout(this._updateCardWidth, 0)

      this.markUpdated("recentLoans")
    },

  beforeUnmount() {
    clearInterval(this.loanLaunchIntervalId)
    if (this._toastTimeout) clearTimeout(this._toastTimeout)

      window.removeEventListener("pointermove", this.onResizeMove)
      window.removeEventListener("pointerup", this.stopResize)
      window.removeEventListener("pointercancel", this.stopResize)
      window.removeEventListener("pointermove", this.onLoanLaunchResizeMove)
      window.removeEventListener("pointerup", this.stopLoanLaunchResize)
      window.removeEventListener("pointercancel", this.stopLoanLaunchResize)

      window.removeEventListener("pointermove", this.onFabDragMove)
      window.removeEventListener("pointerup", this.stopFabDrag)
      window.removeEventListener("pointercancel", this.stopFabDrag)

      document.body.classList.remove("cc-resize-grabbing")
      window.removeEventListener("resize", this._updateCardWidth)
      window.removeEventListener("resize", this._updateViewport)
    }
  }
</script>

<style src="@/assets/css/commandcenter/commandcenter.css"></style>

<style scoped>
  .cc-swap-area {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .cc-dashboard {
    width: 100%;
    height: calc(100vh - 208px);
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: hidden;
  }

.cc-topbar {
  width: 100%;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
  position: sticky;
  top: 0;
  z-index: 1;
}

  .cc-topbar-row {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  }

  .cc-topbar-left {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
  }

  .cc-topbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 14px;
    flex-shrink: 0;
  }

  .cc-topbar-pill {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.1);
    background: #fff;
    font-weight: 900;
    font-size: 12px;
    color: rgba(15, 23, 42, 0.7);
  }

  .cc-topbar-pill-text {
    margin-right: 2px;
  }

  .k-switch-md {
    width: 67px;
  }

.cc-quick-search {
 width: 100%;
  max-width: 520px;
  position: relative;
  z-index: 4999;
}

  .cc-layout-grid {
    flex: 1;
    min-height: 0;
    width: 100%;
    display: grid;
    grid-template-columns: 1.7fr 0.9fr 0.6fr 1.35fr;
    grid-template-rows: auto 1fr;
    gap: 14px;
    align-items: stretch;
    overflow: hidden;
  }

  .cc-card {
    width: 100%;
    height: 100%;
    background: #fff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 14px;
    padding: 14px;
    overflow: hidden;
  }

  .cc-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .cc-card-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 900;
    color: #0f172a;
    font-size: 14px;
  }

  .cc-action-card {
    grid-column: 1 / 4;
    grid-row: 1;
  }

  .cc-closing-card {
    grid-column: 1 / 2;
    grid-row: 2;
    padding: 0px;
    min-width: 0;
  }

  .cc-pipeline-card {
    grid-column: 2 / 3;
    grid-row: 2;
    padding: 0px;
    min-width: 0;
  }

  .cc-loan-tasks-card {
    grid-column: 3 / 4;
    grid-row: 2;
    min-width: 0;
  }

  .cc-my-open-loanblock {
    grid-column: 4 / 5;
    grid-row: 1 / span 2;
    padding: 0;
    min-width: 0;
  }

    .cc-my-open-loanblock :deep(.recent-loans) {
      height: 100%;
      overflow: auto;
    }

  .cc-task-grid {
    margin-top: 12px;
  }

  .cc-swiftai-fab {
    position: fixed;
    right: 18px;
    bottom: 18px;
    z-index: 9999;
    height: 54px;
    padding: 0 18px;
    border-radius: 18px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    border: 1px solid rgba(37, 99, 235, 0.32);
    background: var(--primary);
    cursor: pointer;
    user-select: none;
    touch-action: none;
    font-weight: 950;
    font-size: 14px;
    color: #fff;
    letter-spacing: 0.2px;
    transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease;
  }

    .cc-swiftai-fab.dragging {
      cursor: grabbing;
      transform: none;
    }

    .cc-swiftai-fab i {
      font-size: 16px;
      --fa-primary-color: #1d4ed8;
      --fa-secondary-color: #fde68a;
      --fa-secondary-opacity: 1;
      filter: drop-shadow(0 10px 22px rgba(37, 99, 235, 0.35)) drop-shadow(0 6px 16px rgba(251, 191, 36, 0.35));
    }

    .cc-swiftai-fab:hover {
      transform: translateY(-2px);
      box-shadow: 0 22px 40px rgba(37, 99, 235, 0.32);
      filter: brightness(1.05);
    }

    .cc-swiftai-fab:active {
      transform: translateY(0px) scale(0.98);
    }

  @media (max-width: 925px) {
    .cc-swiftai-fab {
      right: 14px;
      bottom: 14px;
    }

    .cc-layout-grid {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
    }

    .cc-action-card,
    .cc-closing-card,
    .cc-pipeline-card,
    .cc-loan-tasks-card,
    .cc-my-open-loanblock {
      grid-column: auto;
      grid-row: auto;
    }
  }

  .cc-ai-dock {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    z-index: 30000;
    background: #fff;
    border-left: 1px solid rgba(15, 23, 42, 0.08);
    box-shadow: -18px 0 34px rgba(15, 23, 42, 0.12);
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  .cc-ai-resize-handle {
    position: absolute;
    left: -6px;
    top: 0;
    width: 12px;
    height: 100%;
    cursor: ew-resize;
    z-index: 30001;
    touch-action: none;
    user-select: none;
  }

  .cc-resize-grabbing {
    cursor: ew-resize !important;
    user-select: none !important;
  }

  .cc-ai-dock :deep(.ai-drawer),
  .cc-ai-dock :deep(.ai-drawer__body),
  .cc-ai-dock :deep(.k-tabstrip),
  .cc-ai-dock :deep(.k-tabstrip-content),
  .cc-ai-dock :deep(.k-content) {
    flex: 1;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  .cc-ai-dock :deep(iframe) {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }

  .cc-ai-dock.is-interacting :deep(iframe) {
    pointer-events: none;
  }

  .cc-drag-shield {
    position: absolute;
    inset: 0;
    z-index: 30002;
    background: transparent;
    cursor: ew-resize;
  }

  .cc-smart-header {
    padding-left: 14px;
    padding-right: 14px;
    padding-top: 14px;
    position: relative;
    overflow: hidden;
  }

  .cc-smart-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .cc-smart-header::before {
    content: "";
    position: absolute;
    inset: 0;
    opacity: 0.9;
    pointer-events: none;
  }

  .cc-smart-header::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    pointer-events: none;
  }

  .cc-smart-header .cc-card-title {
    position: relative;
    z-index: 2;
  }

.cc-dock-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(2px);
  z-index: 29999;
}

/* Toast */
.cc-toast {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  pointer-events: none;
  white-space: nowrap;
}
.cc-toast--success { background: #e9f7ef; color: #1e7f4f; border: 1px solid #d2f0df; }
.cc-toast--error   { background: #fdebec; color: #9f2d2f; border: 1px solid #f8d1d3; }
.cc-toast-enter-active, .cc-toast-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.cc-toast-enter-from, .cc-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }
</style>